import { FormProps, FormState } from '@/shared/components/hi/types'
import { IHIFormType } from '@/shared/components/hi/IHIForm'
import { MedicareFormType } from '@/shared/components/hi/MedicareForm'
import type { Session } from 'next-auth'
import { ErrorType, IHIdentifier, IHIErrorReason } from '@/lib/apis/types'
import { useMutation } from '@tanstack/react-query'
import { CheckDuplicateIHI, UpdateUserIHI } from '@/lib/apis/account'
import React, { ReactNode, useState } from 'react'
import { isHttpError } from '@/lib/apis/error'
import Modal, { ModalVariant } from '@/shared/components/Modal'
import { AnimatePresence, motion } from 'framer-motion'
import useApiClient from '@/lib/apis/clients/useApiClient'
import posthog from 'posthog-js'
import Link from 'next/link'
import clsx from 'clsx'

export type IHIFormState = { id: 'IHI' } & FormState<IHIFormType>
export type MEDFormState = { id: 'MED' } & FormState<MedicareFormType>
export type IHIVerificationState = IHIFormState | MEDFormState

export type HealthcareIdentifierRenderProps<T> = FormProps<T> & {
  duplicateAccountError?: boolean
  onClearServerError?: () => void
  onClickLogin?: () => void
  onChangeVerificationMethod: (method: 'MED' | 'IHI') => void
}

export type HealthcareIdentifierFormProps = {
  onVerified: (attachedToProfile: boolean) => void
  state: IHIVerificationState
  onUpdate: (state: IHIVerificationState) => void
  session: Session | null
  onClickLogin?: () => void
  variant?: 'default' | 'quiz' | 'profile'

  // Rendering functions
  ihiForm: (props: HealthcareIdentifierRenderProps<IHIFormType>) => ReactNode
  medicareForm: (
    props: HealthcareIdentifierRenderProps<MedicareFormType>,
  ) => ReactNode
}

export type StageError =
  | {
      type: Exclude<ErrorType, 'IHI_ERROR'>
    }
  | {
      type: 'IHI_ERROR'
      reason: IHIErrorReason
    }

export default function HealthcareIdentifierForm({
  onVerified,
  session,
  state,
  onUpdate,
  onClickLogin,
  ihiForm,
  medicareForm,
  variant = 'default',
}: HealthcareIdentifierFormProps) {
  const api = useApiClient()
  const checkDuplicateIhiMutation = useMutation(CheckDuplicateIHI.mutation(api))
  const userMutation = useMutation(UpdateUserIHI.mutation(api))
  const [error, setError] = useState<StageError>()
  const [showError, setShowError] = useState<boolean>(false)
  const duplicateAccountError =
    error?.type === 'IHI_ERROR' && error.reason === 'DUPLICATE_IHI'

  function clearServerError() {
    setError(undefined)
    setShowError(false)
  }

  // When the verification method is changed, empty the fields of both and set touched to false for all fields
  function onChangeVerificationMethod(method: 'MED' | 'IHI') {
    clearServerError()
    const updated: IHIVerificationState =
      method === 'MED'
        ? {
            id: 'MED',
            values: { number: '', expiry: '' },
            touched: { number: false, irn: false, expiry: false },
          }
        : { id: 'IHI', values: { ihi: '' }, touched: { ihi: false } }
    onUpdate(updated)
  }

  function verifiedWithSameMethod(values: IHIdentifier): boolean {
    if (!state.verified) return false
    const verified = state.verified
    if (values.type === 'ihi' && 'ihi' in verified)
      return values.ihi === verified.ihi
    if (values.type === 'med' && 'number' in verified)
      return (
        values.number === verified.number &&
        values.irn === verified.irn &&
        values.expiry === verified.expiry
      )
    return false
  }

  function verifyIHI(values: IHIdentifier): Promise<void> {
    const onSuccess = () => {
      if (values.type === 'ihi') {
        const verified = { ihi: values.ihi }

        onUpdate({
          id: 'IHI',
          values: { ...verified },
          touched: { ihi: true },
          verified,
        })
      } else if (values.type === 'med') {
        const verified = {
          number: values.number,
          irn: values.irn,
          expiry: values.expiry,
        }

        onUpdate({
          id: 'MED',
          values: { ...verified },
          touched: { number: true, irn: true, expiry: true },
          verified,
        })
      } else {
        throw new Error('DVA verification has not been verified yet')
      }

      onVerified(Boolean(session))
    }

    // This method is called when next is clicked, so here's what we should do:
    // - if the verification is not done (i.e state.verified is undefined), do the verification
    // - if the verification is done, but the new values are different from previously verified values,
    //     do the verification again
    // - anonymous patients can reuse unchanged availability checks
    // - signed-in patients must save the identifier to their account
    return new Promise<void>((resolve) => {
      // Anonymous verification only checks availability. After sign-in the
      // identifier must be saved to the account before it is treated as attached.
      if (!session && verifiedWithSameMethod(values)) {
        resolve()
        onVerified(false)
      } else if (session) {
        userMutation.mutate(
          { data: { identifier: values } },
          {
            onSuccess,
            onError: (err) => {
              handleError(err, values)
              onUpdate({ ...state, verified: undefined })
            },
            onSettled: () => resolve(),
          },
        )
      } else {
        checkDuplicateIhiMutation.mutate(
          { data: { identifier: values } },
          {
            onSuccess,
            onError: (error) => handleError(error, values),
            onSettled: () => resolve(),
          },
        )
      }
    })
  }

  function handleError(err: any, identifier: IHIdentifier) {
    // We want to capture duplicate identifier errors here to determine if it's
    // gonna be worth it to implement something that does no IHI checking on
    // application.
    posthog.captureException(err, {
      identifier,
    })

    if (isHttpError(err)) {
      setError({
        type: err.response.error,
        reason: err.response.metadata?.reason,
      })
    } else {
      console.error('Unknown error occurred')
      setError({ type: 'UNKNOWN' })
    }

    setShowError(
      !(
        isHttpError(err) &&
        err.response.error === 'IHI_ERROR' &&
        err.response.metadata?.reason === 'DUPLICATE_IHI'
      ),
    )
  }

  return (
    <>
      {variant === 'quiz' ? (
        <motion.div
          animate={{ opacity: 1, y: 0 }}
          className="mt-3 grid grid-cols-1 gap-3 sm:mt-8 sm:grid-cols-2 sm:gap-4"
          initial={{ opacity: 0, y: 10 }}
          role="radiogroup"
          transition={{
            delay: 0.26,
            duration: 0.58,
            ease: [0.22, 0.55, 0.28, 1],
          }}
        >
          <VerificationMethodButton
            checked={state.id === 'MED'}
            label="Medicare card"
            onClick={() => onChangeVerificationMethod('MED')}
          />
          <VerificationMethodButton
            checked={state.id === 'IHI'}
            label="I have an IHI number instead"
            onClick={() => onChangeVerificationMethod('IHI')}
          />
        </motion.div>
      ) : variant === 'default' ? (
        <div className="flex flex-col gap-y-1.5">
          <div className="relative flex items-start">
            <div className="flex h-6 items-center">
              <input
                id="has-medicare-card"
                aria-describedby="medicare-card-description"
                name="medicare-card"
                type="checkbox"
                className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-600"
                checked={state.id === 'IHI'}
                onChange={() => {
                  if (state.id === 'IHI') onChangeVerificationMethod('MED')
                  else onChangeVerificationMethod('IHI')
                }}
              />
            </div>
            <div className="ml-3 text-sm leading-6">
              <label
                id="medicare-card-description"
                htmlFor="has-medicare-card"
                className="font-medium text-gray-900"
              >
                I don{"'"}t have a medicare card.
              </label>
            </div>
          </div>
        </div>
      ) : null}
      <AnimatePresence mode="wait" initial={false}>
        <motion.div
          className={clsx('flex flex-col', variant === 'default' && 'mt-4')}
          key={state.id}
          initial={{ opacity: 0, y: variant === 'quiz' ? 8 : 0 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: variant === 'quiz' ? -5 : 0 }}
          transition={{
            ease:
              variant === 'quiz' ? [0.45, 0, 0.2, 1] : [0.22, 0.55, 0.28, 1],
            duration:
              variant === 'quiz' ? 0.48 : variant === 'profile' ? 0.42 : 0.3,
          }}
        >
          {(() => {
            if (state.id === 'MED') {
              return medicareForm({
                state: state,
                onVerify: async (values) => {
                  const identifier: IHIdentifier = {
                    type: 'med',
                    ...values,
                    irn: values.irn!!,
                  }

                  await verifyIHI(identifier)
                },
                onInputChanged: (values, touched) => {
                  clearServerError()
                  onUpdate({
                    id: 'MED',
                    values,
                    touched,
                    verified: state.verified,
                  })
                },
                duplicateAccountError,
                onClearServerError: clearServerError,
                onClickLogin,
                onChangeVerificationMethod,
              })
            } else if (state.id === 'IHI') {
              return ihiForm({
                state: state,
                onVerify: async (values) => {
                  const identifier: IHIdentifier = {
                    type: 'ihi',
                    ...values,
                  }

                  await verifyIHI(identifier)
                },
                onInputChanged: (values, touched) => {
                  clearServerError()
                  onUpdate({
                    id: 'IHI',
                    values,
                    touched,
                    verified: state.verified,
                  })
                },
                duplicateAccountError,
                onClearServerError: clearServerError,
                onClickLogin,
                onChangeVerificationMethod,
              })
            } else {
              return null
            }
          })()}
        </motion.div>
      </AnimatePresence>

      {showError && error && (
        <ErrorModal
          error={error}
          state={state}
          onClose={() => setShowError(false)}
          onClickLogin={
            onClickLogin
              ? () => {
                  setShowError(false)
                  onClickLogin()
                }
              : undefined
          }
        />
      )}
    </>
  )
}

function VerificationMethodButton({
  checked,
  label,
  onClick,
}: {
  checked: boolean
  label: string
  onClick: () => void
}) {
  return (
    <button
      aria-checked={checked}
      className={clsx(
        'flex min-h-[48px] w-full items-center rounded-2xl border-[1.5px] bg-[#F4F4F6] px-[15px] py-[11.5px] text-left text-[14.5px] font-medium leading-[18px] transition-[border-color,color] duration-300 sm:min-h-[55px] sm:px-5 sm:py-[14.5px] sm:text-[15px] sm:leading-[19px]',
        checked
          ? 'border-[#0B0B0F] text-[#0B0B0F]'
          : 'border-transparent text-[#5C5C64]',
      )}
      onClick={onClick}
      role="radio"
      type="button"
    >
      {label}
      <span
        className={clsx(
          'ml-auto h-[19px] w-[19px] shrink-0 rounded-full border-[1.5px] transition-colors duration-300 sm:h-5 sm:w-5',
          checked
            ? 'flex items-center justify-center border-[#0B0B0F] bg-[#0B0B0F]'
            : 'border-[#C4C4CA] bg-white',
        )}
      >
        {checked && (
          <span className="h-[7px] w-[7px] rounded-full bg-white sm:h-2 sm:w-2" />
        )}
      </span>
    </button>
  )
}

type ErrorModalProps = {
  error: StageError
  state: IHIVerificationState
  onClose: () => void
  onClickLogin?: () => void
}

function ErrorModal({ error, state, onClose, onClickLogin }: ErrorModalProps) {
  let title = 'Unexpected error'
  let body: ReactNode =
    "Oh no, something went wrong and we're terribly sorry about this. If you think this is not supposed to happen, please reach out to our support at support@nextclinic.com.au."
  if (error.type === 'IHI_ERROR') {
    switch (error.reason) {
      case 'DUPLICATE_IHI':
        title =
          state.id === 'IHI'
            ? 'Duplicate IHI number found'
            : 'Duplicate Medicare number found'

        const signInButton = onClickLogin ? (
          <button
            className="font-medium text-blue-600 hover:underline"
            onClick={() => onClickLogin()}
          >
            Sign in now
          </button>
        ) : (
          'Sign in now'
        )

        const firstPart =
          state.id === 'IHI' ? (
            <p>We found an account with duplicate IHI number in our system.</p>
          ) : (
            <p>
              We found an account with duplicate Medicare number in our system.
            </p>
          )

        body = (
          <div className="mt-3.5 flex flex-col gap-y-2 text-left">
            {firstPart}
            <p>
              {signInButton} or contact our support via
              support@nextclinic.com.au if you have never made an account with
              us. Your identity might have been stolen.
            </p>
            <p>
              If you cannot remember your password,{' '}
              <Link
                href="/forgot-password"
                className="font-medium text-blue-600 hover:underline"
              >
                reset your password
              </Link>
              .
            </p>
          </div>
        )
        break
      case 'REQUEST_VALIDATION':
      case 'INVALID_STATUS':
        title = 'Invalid IHI Status'
        body =
          'Unable to validate your details. Please check your details and if you think this is incorrect, please contact our support via support@nextclinic.com.au.'
        break
    }
  }

  return (
    <Modal
      variant={ModalVariant.Error}
      title={title}
      body={body}
      onClose={onClose}
    />
  )
}
